/**
 * Notification WebSocket Service (Singleton)
 * Manages WebSocket connection for real-time notifications
 * Follows the same pattern as alertsAPI.js for stable connections
 */

import { getToken } from '../utils/tokenStorage';

// Get store dynamically to avoid circular dependency
const getStore = () => {
  return require('../../store').store;
};

// URL resolution (same as alerts)
const getWsBaseUrl = () => {
  if (process.env.REACT_APP_WS_BASE_URL) {
    return process.env.REACT_APP_WS_BASE_URL;
  }
  if (window.location.hostname === 'fleetcamagile.com') {
    return 'wss://fleetcamagile.com';
  }
  return 'ws://localhost:8000';
};

class NotificationService {
  constructor() {
    this.ws = null;
    this.reconnectInterval = null;
    this.reconnectAttempts = 0;
    this.heartbeatInterval = null;
    this.userId = null;
    this.isConnected = false;
  }

  /**
   * Initialize WebSocket connection
   * @param {number} userId - User ID for connection
   */
  initializeWebSocket(userId) {
    // Guard against duplicate connections
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[NotificationService] Already connected');
      return;
    }

    if (!userId) {
      console.log('[NotificationService] No userId provided');
      return;
    }

    this.userId = userId;

    try {
      const WS_BASE_URL = getWsBaseUrl();
      const token = getToken();
      const wsUrl = `${WS_BASE_URL}/ws/notifications/?token=${token || ''}`;
      console.log('[NotificationService] Connecting to:', wsUrl);

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[NotificationService] Connected successfully');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.startHeartbeat();

        // Dispatch connection status to Redux
        const store = getStore();
        if (store && store.dispatch) {
          store.dispatch({
            type: 'notifications/setConnected',
            payload: true,
          });
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          console.error('[NotificationService] Error parsing message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[NotificationService] WebSocket error:', error);
        this.isConnected = false;
      };

      this.ws.onclose = () => {
        console.log('[NotificationService] Connection closed');
        this.isConnected = false;
        this.stopHeartbeat();

        // Dispatch disconnection to Redux
        const store = getStore();
        if (store && store.dispatch) {
          store.dispatch({
            type: 'notifications/setConnected',
            payload: false,
          });
        }

        // Schedule reconnect
        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('[NotificationService] Connection error:', error);
      this.isConnected = false;
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(data) {
    const store = getStore();
    if (!store || !store.dispatch) return;

    switch (data.type) {
      case 'initial_data':
        console.log(
          `[NotificationService] Initial data: ${data.notifications?.length} notifications`
        );
        store.dispatch({
          type: 'notifications/setNotifications',
          payload: data.notifications || [],
        });
        store.dispatch({
          type: 'notifications/setUnreadCount',
          payload: data.unread_count || 0,
        });
        break;

      case 'notification_update':
        if (data.action === 'created') {
          console.log('[NotificationService] New notification:', data.notification.title);
          store.dispatch({
            type: 'notifications/addNotification',
            payload: data.notification,
          });
          // Browser push notification when tab is not focused
          this._showBrowserPush(data.notification);
        } else if (data.action === 'updated') {
          store.dispatch({
            type: 'notifications/updateNotification',
            payload: data.notification,
          });
        } else if (data.action === 'deleted') {
          store.dispatch({
            type: 'notifications/removeNotification',
            payload: { id: data.notification.id },
          });
        }
        break;

      case 'notification_response':
        console.log(
          '[NotificationService] Notification response received:',
          data.notification_title
        );
        store.dispatch({
          type: 'notifications/updateNotificationResponse',
          payload: {
            notificationId: data.notification_id,
            notificationTitle: data.notification_title,
            response: data.response,
          },
        });
        // Show browser push for the response
        this._showResponseToast(data);
        break;

      case 'heartbeat':
        // Heartbeat received, connection is alive
        break;

      case 'pong':
        // Pong response
        break;

      default:
        console.log('[NotificationService] Unknown message type:', data.type);
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect() {
    if (this.reconnectInterval || !this.userId) return;

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    console.log(
      `[NotificationService] Reconnecting in ${delay / 1000}s (attempt ${this.reconnectAttempts})`
    );

    this.reconnectInterval = setTimeout(() => {
      this.reconnectInterval = null;
      this.initializeWebSocket(this.userId);
    }, delay);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Mark notification as read via WebSocket
   */
  markAsRead(notificationId) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'mark_read',
          notification_id: notificationId,
        })
      );
      console.log(`[NotificationService] Marking notification ${notificationId} as read`);
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }

    this.stopHeartbeat();
    this.isConnected = false;
    this.userId = null;

    console.log('[NotificationService] Disconnected');
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      connected: this.isConnected,
      readyState: this.ws?.readyState,
    };
  }

  /**
   * Show a browser desktop push for a notification response event.
   * @param {{ notification_title: string, response: object }} data
   */
  _showResponseToast(data) {
    try {
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;

      const resp = data.response || {};
      const body = resp.action_label
        ? `${resp.user_name} responded: ${resp.action_label}`
        : `${resp.user_name} replied${resp.comment ? `: "${resp.comment}"` : ''}`;

      new Notification(`Response: ${data.notification_title}`, {
        body,
        icon: '/logo192.png',
        tag: `fleetcam-response-${data.notification_id}`,
      });
    } catch {
      // Non-critical
    }
  }

  /**
   * Show a browser desktop push notification when the tab is not focused.
   * Only fires if the user has granted Notification permission.
   * @param {{ title: string, message: string, link: string }} notification
   */
  _showBrowserPush(notification) {
    try {
      if (!('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      if (document.visibilityState === 'visible') return; // Already watching

      const n = new Notification(`FleetCam: ${notification.title}`, {
        body: notification.message,
        icon: '/logo192.png',
        tag: `fleetcam-notif-${notification.id}`,
      });

      if (notification.link) {
        n.onclick = () => {
          window.focus();
          window.location.assign(notification.link);
        };
      }
    } catch {
      // Browser push is non-critical, never throw
    }
  }
}

// Create singleton instance
const notificationService = new NotificationService();

// DO NOT auto-initialize here (will be initialized on login)
// This prevents connecting before user authentication

export default notificationService;
